/**
 * List of holiday requests with filter (All / Pending / Approved / Rejected). Employee can delete pending.
 */
import React, { useState, useEffect, useMemo } from 'react';
import hrApi from '../api';
import { publicHolidayDateSet, countRequestedLeaveDays } from '../utils/workingDays';
import { formatDisplayName } from '../utils/formatTime';
import DaysTakenBreakdownModal from './DaysTakenBreakdownModal';

const LEAVE_TYPE_LABELS = {
  annual: 'Annual leave',
  overtime: 'Overtime leave',
  illness: 'Illness',
  'compensation-extra-hours': 'Compensation for extra hours',
  'doctor-appointment': 'Doctor appointment',
  'birth-adoption': 'Birth or adoption',
  'house-move': 'House move',
  'unpaid-leave': 'Unpaid leave',
  'care-family-member': 'Care of family member',
  accident: 'Accident',
  'military-service': 'Military service',
  'death-family-member': 'Death of family member',
  marriage: 'Marriage',
  training: 'Training',
  'working-remotely': 'Working remotely',
  other: 'Other',
  medical: 'Medical leave',
  others: 'Others',
};

function getLeaveTypeLabel(code) {
  if (!code) return '–';
  return LEAVE_TYPE_LABELS[String(code).toLowerCase()] || String(code);
}

/**
 * Returns true if this leave type deducts from the employee's leave balance
 * (Annual/Vacation/Carry-over pool or Overtime leave balance).
 */
function isLeaveTypeCounted(code) {
  const raw = String(code || 'annual').trim().toLowerCase();
  return raw === 'annual' || raw === 'vacation' || raw === 'carry-over' || raw === 'overtime';
}

function formatDateStr(d) {
  if (!d) return '–';
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function getHalfDaySummaryText(r) {
  if (r.halfDays && typeof r.halfDays === 'object' && !Array.isArray(r.halfDays)) {
    const entries = Object.entries(r.halfDays).filter(([, p]) => p === 'am' || p === 'pm');
    if (entries.length === 1) {
      const [, part] = entries[0];
      return `(Half ${part === 'am' ? 'AM' : 'PM'})`;
    }
    if (entries.length > 1) {
      return `(${entries.length} half days)`;
    }
  }
  if (r.halfDay === 'am' || r.halfDay === 'pm') {
    return r.halfDayOn === 'start' || r.halfDayOn === 'end'
      ? `(Half ${r.halfDay === 'am' ? 'AM' : 'PM'} on ${r.halfDayOn === 'start' ? 'first' : 'last'} day)`
      : `(Half ${r.halfDay === 'am' ? 'AM' : 'PM'})`;
  }
  return null;
}

function HolidayRequestList({ requests: initialRequests, onUpdate, showEmployeeName = false }) {
  const [requests, setRequests] = useState(initialRequests || []);
  const [filter, setFilter] = useState('all');
  const [deletingId, setDeletingId] = useState(null);
  const [publicHolidaysByYear, setPublicHolidaysByYear] = useState({});
  const [breakdownRequest, setBreakdownRequest] = useState(null);
  const [employeeWorkingSettings, setEmployeeWorkingSettings] = useState(null);
  const [overrideWindows, setOverrideWindows] = useState([]);

  useEffect(() => {
    setRequests(initialRequests || []);
  }, [initialRequests]);

  useEffect(() => {
    hrApi
      .get('/api/default-working-hours')
      .then((res) => {
        const wd = res.data?.workingDays;
        const workingDays = Array.isArray(wd) && wd.length > 0 ? wd : [1, 2, 3, 4, 5];
        const ds = res.data?.daySchedule;
        const daySchedule = ds && typeof ds === 'object' && !Array.isArray(ds) ? ds : undefined;
        setEmployeeWorkingSettings({ workingDays, daySchedule });
      })
      .catch(() => setEmployeeWorkingSettings(null));
    hrApi
      .get('/api/work-schedule-overrides/mine')
      .then((res) => setOverrideWindows(Array.isArray(res.data) ? res.data : []))
      .catch(() => setOverrideWindows([]));
  }, []);

  const yearsNeeded = useMemo(() => {
    const years = new Set();
    (initialRequests || []).forEach((r) => {
      if (r.startDate) years.add(new Date(r.startDate).getFullYear());
      if (r.endDate) years.add(new Date(r.endDate).getFullYear());
    });
    return Array.from(years);
  }, [initialRequests]);

  useEffect(() => {
    if (yearsNeeded.length === 0) {
      setPublicHolidaysByYear({});
      return;
    }
    const fetches = yearsNeeded.map((year) =>
      hrApi.get('/api/public-holidays', { params: { year } }).then((res) => ({ year, list: Array.isArray(res.data) ? res.data : [] }))
    );
    Promise.all(fetches).then((results) => {
      const byYear = {};
      results.forEach(({ year, list }) => { byYear[year] = list; });
      setPublicHolidaysByYear(byYear);
    }).catch(() => setPublicHolidaysByYear({}));
  }, [yearsNeeded.join(',')]);

  const allPublicHolidays = useMemo(() => {
    return Object.values(publicHolidaysByYear).flat();
  }, [publicHolidaysByYear]);

  const getDaysTaken = (r) => {
    if (!r.startDate || !r.endDate) return '–';
    const start = new Date(r.startDate);
    const end = new Date(r.endDate);
    if (end < start) return 0;
    const set = publicHolidayDateSet(allPublicHolidays);
    return countRequestedLeaveDays(start, end, set, employeeWorkingSettings, overrideWindows, null, r.halfDay, r.halfDayOn, r.halfDays);
  };

  const filtered = filter === 'all'
    ? requests
    : requests.filter((r) => r.status === filter);

  const handleDelete = async (id) => {
    if (!window.confirm('Cancel this request?')) return;
    setDeletingId(id);
    try {
      await hrApi.delete(`/api/holidays/${id}`);
      setRequests((prev) => prev.filter((r) => r._id !== id));
      if (onUpdate) onUpdate();
    } catch (_) {
      setDeletingId(null);
    }
    setDeletingId(null);
  };

  const statusBadge = (status) => {
    const c = status === 'approved' ? 'success' : status === 'rejected' ? 'danger' : 'warning';
    return <span className={`badge bg-${c}`}>{status}</span>;
  };

  const employeeName = (r) => {
    if (!showEmployeeName || !r.adminId) return null;
    const a = r.adminId;
    if (typeof a === 'object' && a.username) return formatDisplayName(a.username);
    return null;
  };

  const downloadAttachment = (requestId, attachment) => {
    hrApi
      .get(`/api/holidays/${requestId}/attachments/${attachment.filename}`, { responseType: 'blob' })
      .then((res) => {
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.originalName || attachment.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      })
      .catch(() => {});
  };

  return (
    <>
      <div className="d-flex gap-2 mb-3 flex-wrap">
        {['all', 'pending', 'approved', 'rejected'].map((f) => (
          <button
            key={f}
            type="button"
            className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilter(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <p className="text-muted small">No requests.</p>
      ) : (
        <div className="hr-allowance-table-wrap">
          <div className="table-responsive">
            <table className="table table-sm hr-allowance-table mb-0">
              <thead>
                <tr>
                  {showEmployeeName && <th>Employee</th>}
                  <th>From</th>
                  <th>To</th>
                  <th>Days taken</th>
                  <th>Leave type</th>
                  <th>Annual Leave Deduction</th>
                  <th>Reason</th>
                  <th>Attachments</th>
                  <th>Status</th>
                  {filter === 'rejected' && <th>Rejection reason</th>}
                  {filter === 'pending' && !showEmployeeName && <th></th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r._id}>
                    {showEmployeeName && (
                      <td className="hr-allowance-name">{employeeName(r) || (typeof r.adminId === 'string' ? r.adminId : '–')}</td>
                    )}
                    <td className="text-nowrap">
                      {formatDateStr(r.startDate)}
                      {getHalfDaySummaryText(r) && (
                        <span className="ms-1 small text-muted">
                          {getHalfDaySummaryText(r)}
                        </span>
                      )}
                    </td>
                    <td className="text-nowrap">{formatDateStr(r.endDate)}</td>
                    <td className="text-nowrap">
                      <span className="d-inline-flex align-items-center gap-1">
                        <span className="fw-medium">{getDaysTaken(r)}</span>
                        {r.startDate && r.endDate && (
                          <button
                            type="button"
                            className="hr-days-breakdown-btn"
                            onClick={() => setBreakdownRequest(r)}
                            title="Show breakdown"
                            aria-label="Show days breakdown"
                          >
                            <i className="bi bi-calendar3" />
                          </button>
                        )}
                      </span>
                    </td>
                    <td className="small">{getLeaveTypeLabel(r.leaveTypeCode)}</td>
                    <td className="small text-nowrap">
                      <span className={`badge ${isLeaveTypeCounted(r.leaveTypeCode) ? 'bg-primary' : 'bg-secondary'}`}>
                        {isLeaveTypeCounted(r.leaveTypeCode) ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="small text-muted">{r.comment || '–'}</td>
                    <td className="small">
                      {(r.attachments && r.attachments.length > 0)
                        ? r.attachments.map((att, i) => (
                            <button
                              key={i}
                              type="button"
                              className="btn btn-link btn-sm p-0 me-2 text-primary text-decoration-none"
                              onClick={() => downloadAttachment(r._id, att)}
                              title={`Download ${att.originalName}`}
                            >
                              <i className="bi bi-paperclip me-1" />
                              {att.originalName}
                            </button>
                          ))
                        : '–'}
                    </td>
                    <td className="text-nowrap">{statusBadge(r.status)}</td>
                    {filter === 'rejected' && <td className="small">{r.rejectedReason || '–'}</td>}
                    {filter === 'pending' && !showEmployeeName && (
                      <td className="text-end text-nowrap">
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleDelete(r._id)}
                          disabled={deletingId === r._id}
                        >
                          {deletingId === r._id ? '…' : 'Cancel'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {breakdownRequest && (
        <DaysTakenBreakdownModal
          show={!!breakdownRequest}
          onClose={() => setBreakdownRequest(null)}
          startDate={breakdownRequest.startDate}
          endDate={breakdownRequest.endDate}
          publicHolidays={allPublicHolidays}
          daysTaken={typeof getDaysTaken(breakdownRequest) === 'number' ? getDaysTaken(breakdownRequest) : 0}
          employeeWorkingSettings={employeeWorkingSettings}
          overrideWindows={overrideWindows}
          halfDay={breakdownRequest.halfDay}
          halfDayOn={breakdownRequest.halfDayOn}
          halfDays={breakdownRequest.halfDays}
        />
      )}
    </>
  );
}

export default HolidayRequestList;
